import { useQueryClient } from '@tanstack/react-query';
const STORAGE_KEY = 'varos_threads';
const MAX_THREADS = 50;
// Helper functions for sessionStorage operations
function getThreadsFromStorage() {
    try {
        const stored = window.sessionStorage.getItem(STORAGE_KEY);
        if (!stored)
            return [];
        const parsed = JSON.parse(stored);
        // Parse and validate threads, converting date strings back to Date objects
        const validatedThreads = [];
        parsed.forEach((thread) => {
            try {
                validatedThreads.push({
                    id: thread.id,
                    object: thread.object,
                    title: thread.title,
                    metadata: thread.metadata,
                    context: thread.context,
                    createdAt: new Date(thread.createdAt),
                });
            }
            catch (error) {
                console.warn(`Failed to parse thread ${thread.id}:`, error);
            }
        });
        return validatedThreads;
    }
    catch (error) {
        console.warn('Failed to parse stored threads:', error);
        return [];
    }
}
function saveThreadsToStorage(threads) {
    try {
        // Enforce maximum number of threads by keeping only the most recent ones
        let limitedThreads = threads;
        if (threads.length > MAX_THREADS) {
            // Sort by createdAt descending (newest first) and take the first MAX_THREADS
            limitedThreads = threads
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .slice(0, MAX_THREADS);
        }
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(limitedThreads));
    }
    catch (error) {
        console.error('Error saving threads to sessionStorage:', error);
    }
}
function generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
}
export function createThreadRepository(config) {
    const { repository } = config;
    function getThreadFindQueryKey(props) {
        return ['platform', 'thread', 'find', props];
    }
    function getThreadLookupQueryKey(props) {
        return ['platform', 'thread', 'lookup', props.id];
    }
    return {
        useFind(context, props, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getThreadFindQueryKey(props),
                async queryFn() {
                    const allThreads = getThreadsFromStorage();
                    // Apply limit if specified
                    let filteredThreads = allThreads;
                    if (props.limit) {
                        // Sort by createdAt descending (newest first) and take the first props.limit
                        filteredThreads = filteredThreads
                            .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
                            .slice(0, props.limit);
                    }
                    else {
                        // Sort by createdAt descending for newest first
                        filteredThreads = filteredThreads.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
                    }
                    return filteredThreads;
                },
            });
            return query;
        },
        useLookup(context, props, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getThreadLookupQueryKey(props),
                async queryFn() {
                    const allThreads = getThreadsFromStorage();
                    const thread = allThreads.find((t) => t.id === props.id);
                    if (!thread) {
                        throw new Error(`Thread with id ${props.id} not found`);
                    }
                    return thread;
                },
            });
            return query;
        },
        useCreate(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(props) {
                    const allThreads = getThreadsFromStorage();
                    const newThread = {
                        id: generateId(),
                        object: 'thread',
                        title: props.title || 'New Thread',
                        metadata: props.metadata || {},
                        context: props.context || [],
                        createdAt: new Date(),
                    };
                    const updatedThreads = [...allThreads, newThread];
                    saveThreadsToStorage(updatedThreads);
                    return newThread;
                },
                onMutate: async (props) => {
                    // Cancel any outgoing refetches
                    await queryClient.cancelQueries({
                        queryKey: ['platform', 'thread', 'find'],
                    });
                    // Snapshot the previous values
                    const previousThreads = queryClient.getQueryData([
                        'platform',
                        'thread',
                        'find',
                        {},
                    ]);
                    // Optimistically update with new thread
                    const newThread = {
                        id: generateId(),
                        object: 'thread',
                        title: props.title || 'New Thread',
                        metadata: props.metadata || {},
                        context: props.context || [],
                        createdAt: new Date(),
                    };
                    queryClient.setQueryData(['platform', 'thread', 'find', {}], (old) => {
                        const currentThreads = old || [];
                        return [newThread, ...currentThreads]; // Add to beginning for newest first
                    });
                    return { previousThreads, newThread };
                },
                onError: (err, newThread, context) => {
                    // Rollback on error
                    if (context?.previousThreads) {
                        queryClient.setQueryData(['platform', 'thread', 'find', {}], context.previousThreads);
                    }
                },
                async onSuccess(data) {
                    // Invalidate find queries
                    await queryClient.refetchQueries({
                        queryKey: ['platform', 'thread', 'find'],
                    });
                },
            });
        },
        useUpdate(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(props) {
                    const [threadId, payload] = props;
                    const allThreads = getThreadsFromStorage();
                    const threadIndex = allThreads.findIndex((t) => t.id === threadId);
                    if (threadIndex === -1) {
                        throw new Error(`Thread with id ${threadId} not found`);
                    }
                    const existingThread = allThreads[threadIndex];
                    const updatedThread = {
                        ...existingThread,
                        title: payload.title ?? existingThread.title,
                        metadata: payload.metadata ?? existingThread.metadata,
                    };
                    const updatedThreads = [...allThreads];
                    updatedThreads[threadIndex] = updatedThread;
                    saveThreadsToStorage(updatedThreads);
                    return updatedThread;
                },
                async onSuccess(data) {
                    await queryClient.refetchQueries({
                        queryKey: ['platform', 'thread'],
                    });
                },
            });
        },
        useGenerate(context) {
            return repository.useMutation(context, {
                async mutationFn(props) {
                    const response = await config.client.user.threadGenerate({
                        xWorkspaceId: context.workspace.id,
                        threadGenerateRequest: props,
                    });
                    return response.data;
                },
            });
        },
    };
}
//# sourceMappingURL=threadRepository.js.map